import {
  faBan,
  faCheck,
  faSparkles,
  faXmark,
  faRefresh,
  faPenToSquare,
  faFileExport,
  faPen,
  faEnvelope,
  faClock,
  IconDefinition,
  faTriangleExclamation,
  faArchive,
  faArrowRotateLeft,
  faInfoCircle,
  faFileCheck,
  faUserCircle,
  faUsers,
  faFileMagnifyingGlass,
  faListCheck,
  faMemoCircleCheck
} from '@fortawesome/pro-regular-svg-icons';
import { IDocumentType, IWorkPhase } from '@publishing-engine/domain/models';
import { BadgeVariants } from '@morressier/alembic';
import { AtLeast } from '@publishing-engine/domain/types/workflow-phases';
import { faUserSlash } from '@fortawesome/pro-light-svg-icons';
import { IReviewBadge } from '@publishing-engine/domain/utils';
import { SortField } from './pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';

export const TABLE_PAGE_LIMIT = 10;

export const AUTH_BEARER_TOKEN_KEY = 'role_bearer_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const APPLICATION_NAME = 'Journal Manager';
// Used to show modals with selected item in a list (submissions, etc);
export const SELECTED_ITEM_QUERY_KEY = 'selectedItem';
export const FETCH_ACTIVITY_LOG_KEY = 'submission-activity-logs';
export const FETCH_EDITORIAL_ASSIGNEES_KEY = 'editorial-assignees';
// Query key of the My Submissions tab counts. Shared so the submission success
// callback can invalidate them when a submission moves between tabs.
export const MY_SUBMISSIONS_TOTALS_QUERY_KEY = 'my-submissions-totals';
// Query key of the per-user activity totals (drafts/submissions/reviews) that
// the dashboard navigation reads. Deliberately separate from the tab counts
// above: the two endpoints return different shapes, so sharing a key let
// whichever component fetched first hand the other one a payload it could not
// read.
export const USER_ACTIVITY_TOTALS_QUERY_KEY = 'user-activity-totals';

// Used to show declined review page
export const SUBMISSION_ID_QUERY_KEY = 'submissionId';
export const SUBMISSION_CTX_ID_QUERY_KEY = 'ctxId';

type SubmissionStatusItem = {
  icon: IconDefinition | null;
  variant: BadgeVariants;
  label: string;
};

export const SUBMISSION_STATUS_MAP: Record<
  IWorkPhase | (string & {}),
  SubmissionStatusItem
> = {
  draft: {
    icon: null,
    variant: 'success',
    label: 'Draft'
  },
  initial: {
    icon: null,
    variant: 'neutral',
    label: 'First Submitted'
  },
  revision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Revision'
  },
  revisionAssessment: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Revision Assessment'
  },
  revisionPeerReviewInvitation: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Revision Peer Review Invitation'
  },
  revisionPeerReviewPending: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Revision Peer Review Pending'
  },
  revisionPeerReviewFinished: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Revision Peer Review Decision'
  },
  assignment: {
    icon: faUserCircle,
    variant: 'neutral',
    label: 'Awaiting Assignment'
  },
  investigation: {
    icon: null,
    variant: 'caution',
    label: 'Investigation'
  },
  approve: {
    icon: faCheck,
    variant: 'success',
    label: 'Approved'
  },
  rejected: {
    icon: faBan,
    variant: 'caution',
    label: 'Rejected'
  },
  reviewed: {
    icon: faCheck,
    variant: 'success',
    label: 'Reviewed'
  },
  declined: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined'
  },
  inReview: {
    icon: faCheck,
    variant: 'neutral',
    label: 'Accepted'
  },
  invited: {
    icon: faSparkles,
    variant: 'attention',
    label: 'New'
  },
  initialAssessment: {
    icon: faListCheck,
    variant: 'neutral',
    label: 'Initial Assessment'
  },
  peerReview: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Peer Review'
  },
  peerReviewInvitation: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Peer Review Invitation'
  },
  peerReviewPending: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Waiting for Peer Review'
  },
  peerReviewFinished: {
    icon: faFileMagnifyingGlass,
    variant: 'neutral',
    label: 'Peer Review Decision'
  },
  immediateRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Immediate Revision'
  },
  accepted: {
    icon: faSparkles,
    variant: 'success',
    label: 'Accepted'
  },
  preProduction: {
    icon: faFileCheck,
    variant: 'success',
    label: 'Pre Production'
  },
  withdrawn: {
    icon: faArchive,
    variant: 'attention',
    label: 'Withdrawn'
  },
  transferOffered: {
    icon: null,
    variant: 'attention',
    label: 'Transfer Offer'
  },
  transferPending: {
    icon: faTriangleExclamation,
    variant: 'attention',
    label: 'Incomplete Transfer'
  },
  // TODO: check with designs
  formalAcceptance: {
    icon: faCheck,
    variant: 'success',
    label: 'Accepted for Publication'
  },
  withdrawalRequested: {
    icon: null,
    variant: 'attention',
    label: 'Withdrawal Requested'
  },
  unsubmitted: {
    icon: faArrowRotateLeft,
    variant: 'attention',
    label: 'Unsubmitted'
  }
} as const;

// --------------------------------------------------------------------------------------------
// Editor Submission List Phase Column Map
// --------------------------------------------------------------------------------------------

type EditorSubmissionListPhaseColumn = {
  alias: string;
  icon: IconDefinition;
  iconVariant: BadgeVariants;
  description: string;
};

export const EDITOR_SUBMISSION_LIST_PHASE_MAP: Record<
  AtLeast<IWorkPhase>,
  EditorSubmissionListPhaseColumn
> = {
  assignment: {
    alias: 'Awaiting Assignment',
    icon: faUserCircle,
    iconVariant: 'interactive',
    description: 'An owner needs to be assigned to a Submission.'
  },
  initialAssessment: {
    alias: 'Initial Assessment',
    icon: faListCheck,
    iconVariant: 'interactive',
    description: 'A full assessment of this Submission must be made.'
  },
  peerReviewInvitation: {
    alias: 'Invite Reviewers',
    icon: faUsers,
    iconVariant: 'attention',
    description: 'Reviewers must be invited to kickstart peer review.'
  },
  peerReviewPending: {
    alias: 'Peer Review in Progress',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      'Oversee Reviewer tasks and provide support as needed. Be ready to manage Reviewer deadlines and extension requests.'
  },
  peerReview: {
    alias: 'Peer Review in Progress',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      'Oversee Reviewer tasks and provide support as needed. Be ready to manage Reviewer deadlines and extension requests.'
  },
  peerReviewFinished: {
    alias: 'Decide on Peer Review',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      "There's enough completed Reviews to make a decision. Score completed Reviews, and manage pending deadlines and extensions."
  },
  immediateRevision: {
    alias: 'Immediate Revision',
    icon: faPenToSquare,
    iconVariant: 'interactive',
    description: 'A revision has been requested for this Submission.'
  },
  revision: {
    alias: 'Awaiting Revision',
    icon: faPenToSquare,
    iconVariant: 'interactive',
    description: 'A revision has been requested for this Submission.'
  },
  revisionAssessment: {
    alias: 'Rev - Assessment',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      "Assess the Author's revised work and make a decision. Be ready to manage Reviewer deadlines and extension requests."
  },
  revisionPeerReviewInvitation: {
    alias: 'Rev - Invite Reviewers',
    icon: faUsers,
    iconVariant: 'attention',
    description: 'Reviewers must be invited to assess the revised manuscript.'
  },
  revisionPeerReviewPending: {
    alias: 'Rev - Peer Review in Progress',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      'Oversee Reviewer tasks and provide support as needed. Be ready to manage Reviewer deadlines and extension requests.'
  },
  revisionPeerReviewFinished: {
    alias: 'Rev - Decide on Peer Review',
    icon: faFileMagnifyingGlass,
    iconVariant: 'attention',
    description:
      "There's enough completed revision Reviews to make a decision. Score completed Reviews, and manage pending deadlines and extensions."
  },
  preProduction: {
    alias: 'Pre Production',
    icon: faMemoCircleCheck,
    iconVariant: 'success',
    description:
      'Ensure the Submission contains all necessary materials to go into Production.'
  },
  transferOffered: {
    alias: 'Transfer Offered',
    icon: faTriangleExclamation,
    iconVariant: 'interactive',
    description:
      'The author was offered to transfer their Submission to other Journals. They might request more time to decide on this.'
  },
  withdrawalRequested: {
    alias: 'Withdrawal Requested',
    icon: faArchive,
    iconVariant: 'attention',
    description: 'The author has requested to withdraw their Submission.'
  },
  unsubmitted: {
    alias: 'Unsubmitted',
    icon: faArrowRotateLeft,
    iconVariant: 'attention',
    description:
      'The author received this Submission back as a draft to make structural improvements..'
  },

  // The below events are not displayed on the Editor Submission List, but are still listed here to cover all workflow states
  draft: {
    alias: 'Draft',
    icon: faInfoCircle,
    iconVariant: 'interactive',
    description: 'This Submission is in draft mode.'
  },
  transferPending: {
    alias: 'Incomplete Transfer',
    icon: faTriangleExclamation,
    iconVariant: 'attention',
    description: 'The transfer process is incomplete'
  },
  formalAcceptance: {
    alias: 'Accepted',
    icon: faMemoCircleCheck,
    iconVariant: 'success',
    description: 'This Submission has been accepted.'
  },
  preProductionChangeRequest: {
    alias: 'Pre Production Change Request',
    icon: faInfoCircle,
    iconVariant: 'interactive',
    description:
      'A Pre Production Change Request has been requested for this Submission.'
  },
  rejected: {
    alias: 'Rejected',
    icon: faBan,
    iconVariant: 'caution',
    description: 'This Submission has been rejected.'
  },
  rejectAndTransfer: {
    alias: 'Reject and Transfer',
    icon: faInfoCircle,
    iconVariant: 'caution',
    description:
      'This submission was rejected from its original journal and was transferred to another journal in the system.'
  },
  withdrawn: {
    alias: 'Withdrawn',
    icon: faArchive,
    iconVariant: 'attention',
    description:
      'The submission was withdrawn from the publication process and is now archived.'
  }
} as const;

// --------------------------------------------------------------------------------------------

export const SUBMISSION_TYPE_MAP: Record<IDocumentType, string> = {
  Paper: 'Paper',
  Note: 'Note',
  Letter: 'Letter',
  TopicalReview: 'Topical Review',
  Tutorial: 'Tutorial',
  Comment: 'Comment',
  Editorial_Other: 'Editorial/Other',
  Perspective: 'Perspective',
  Reply: 'Reply',
  Addendum: 'Addendum',
  Corrigendum: 'Corrigendum',
  Erratum: 'Erratum',
  Roadmap: 'Roadmap'
} as const;

export type ISubmissionStatus = keyof typeof SUBMISSION_STATUS_MAP;

export const ERRORS = {
  somethingWentWrong:
    'We’re sorry, something’s gone wrong. Please wait a few minutes and try again or contact support.'
};

export const MY_SUBMISSION_TABS = {
  FEEDBACK: 'Reviews',
  SUBMISSION: 'submission',
  AUTHORS: 'authors',
  RIGHTS: 'rights',
  ACTIVITY: 'activity'
} as const;

export type IMySubmissionTab =
  (typeof MY_SUBMISSION_TABS)[keyof typeof MY_SUBMISSION_TABS];

export const BOARDMEMBER_INVITATION_MAP: Record<string, IReviewBadge> = {
  invited: {
    icon: faEnvelope,
    variant: 'neutral',
    label: 'Invited'
  },
  active: {
    icon: faCheck,
    variant: 'success',
    label: 'Active Member'
  }
};

export const ASSESSMENT_RECOMMENDATION_MAP: Record<string, IReviewBadge> = {
  peerReview: {
    icon: faSparkles,
    variant: 'neutral',
    label: 'Peer Review'
  },
  reject: {
    icon: faXmark,
    variant: 'caution',
    label: 'Reject'
  },
  immediateRevision: {
    icon: faSparkles,
    variant: 'neutral',
    label: 'Immediate Revision'
  },
  unsubmit: {
    icon: faArrowRotateLeft,
    variant: 'neutral',
    label: 'Unsubmit'
  },
  minorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Minor Revision'
  },
  majorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Major Revision'
  },
  publish: {
    icon: faCheck,
    variant: 'success',
    label: 'Publish'
  },
  default: {
    icon: faBan,
    variant: 'caution',
    label: 'N/A'
  },
  transferOffered: {
    icon: null,
    variant: 'attention',
    label: 'Transfer Offer'
  },
  amendments: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Amendments'
  },
  revision: {
    icon: null,
    variant: 'attention',
    label: 'Revision'
  }
};

const revisionStatus = {
  icon: faEnvelope,
  variant: 'interactive',
  label: 'Invited',
  tooltipText: 'Submission is awaiting review.'
};

/**
 * The badge shared by every review state that carries a submitted review, so a
 * consumer can tell those states apart by the badge itself rather than by its
 * label, which is copy and may be reworded.
 */
export const reviewedStatus: IReviewBadge = {
  icon: faCheck,
  variant: 'success',
  label: 'Reviewed'
};

const newStatus = {
  icon: faSparkles,
  variant: 'attention',
  label: 'New'
};

const inProgressStatus = {
  icon: faRefresh,
  variant: 'attention',
  label: 'In Progress'
};

const inReviewStatus = {
  icon: faRefresh,
  variant: 'attention',
  label: 'In Review'
};

export const REVIEW_STATUS_MAP: Record<string, IReviewBadge> = {
  invited: {
    icon: faEnvelope,
    variant: 'interactive',
    label: 'Invited',
    tooltipText: 'Submission is awaiting review.'
  },
  revisionInvited: revisionStatus,
  initial: newStatus,
  new: newStatus,
  firstAssessment: inProgressStatus,
  inReview: inReviewStatus,
  submittedFirstAssessment: reviewedStatus,
  awaitingRevision: reviewedStatus,
  revisionAssessment: inProgressStatus,
  revision: inProgressStatus,
  submittedRevisionAssessment: reviewedStatus,
  reviewed: reviewedStatus,
  awaitingRevisionReviewRequest: {
    icon: faEnvelope,
    variant: 'neutral',
    label: 'Awaiting Reinvitation',
    tooltipText:
      'This reviewer completed a review in a previous round: you can reinvite them for this round or exclude them.'
  },
  declined: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined'
  },
  // Separating declined from declinedWithReason (for tooltip) - No new state but only code change
  declinedWithReason: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined',
    tooltipText: 'This Reviewer has sent information about this decision.'
  },

  extensionRequested: {
    icon: faClock,
    variant: 'attention',
    label: 'Extension Requested'
  },
  expired: {
    icon: faBan,
    variant: 'caution',
    label: 'Expired',
    tooltipText: 'This Reviewer did not respond to the invitation.'
  },
  closed: {
    icon: faBan,
    variant: 'caution',
    label: 'Closed'
  },

  // TODO: [COMA-1405] - Test this after Rescinded is implemented
  rescinded: {
    icon: faFileExport,
    variant: 'attention',
    label: 'Rescinded'
  },

  // TODO: [COMA-1405] - Test this after Draft is implemented
  draft: {
    icon: faPen,
    variant: 'attention',
    label: 'Draft'
  },
  assessment: inProgressStatus,

  interrupted: {
    icon: faBan,
    variant: 'caution',
    label: 'Interrupted'
  },
  withdrawn: {
    icon: faUserSlash,
    variant: 'caution',
    label: 'Withdrawn'
  }
};
export const ReviewedValidstate = [
  'awaitingRevision',
  'submittedRevisionAssessment',
  'reviewed',
  'rescind'
];
// --------------------------------------------------------------------------------
// End Review Constants
// --------------------------------------------------------------------------------
export const FEATURE_FLAGS = {
  formulas: 'Formulas',
  isAuthUsersSync: 'sync_auth_users',
  jedis389UserCreationAndAuthToken:
    'jedis_389_user_creation_outside_auth__use_auth_token',
  mentionsInNotes: 'mentions_in_notes',
  articleTypeDebugView: 'article_type_debug_view',
  articleTypeCreationForm: 'article_type_creation_form',
  enableDevFeatures: 'enable_dev_features',
  coReview: 'co-review',
  ioppKeycloak: 'iopp-keycloak',
  accountSwitcher: 'account-switcher',
  changeArticleType: 'change-article-type',
  revertDecision: 'revert-decision'
};

export const PREVIEW_LIST_SIZE = 3;

export const MAX_FILE_SIZE_MB = 50; // 50MB

// TODO: This should come from configurable article types. Future improvement
// Currently only fields that are part of submission details page are handled
export const changeRequestAllowedFields = [
  'Title',
  'EnglishTitle',
  'Subtitle',
  'EnglishSubtitle',
  'Abstract',
  'EnglishAbstract',
  'Keywords',
  'EnglishKeywords',
  'Figures',
  'Tables',
  'FiguresAndTables',
  'SourceFiles',
  'Manuscript',
  'AnonymizedManuscript',
  'Supplementary',
  'LicensesAndPermissionsReproducedContent',
  'LicensesAndPermissionsFiles',
  'TitlePage'
];

export const ASSESSMENT_STATUS_MAP: Record<string, IReviewBadge> = {
  invited: {
    icon: faEnvelope,
    variant: 'neutral',
    label: 'Invited'
  },
  declined: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined'
  },
  expired: {
    icon: faBan,
    variant: 'caution',
    label: 'Expired'
  },
  draft: {
    icon: faPen,
    variant: 'neutral',
    label: 'Draft'
  },
  reviewed: {
    icon: faCheck,
    variant: 'success',
    label: 'Reviewed'
  },
  assessment: {
    icon: faRefresh,
    variant: 'attention',
    label: 'In Progress'
  },
  adjudication: {
    icon: faRefresh,
    variant: 'attention',
    label: 'In Progress'
  },
  interrupted: {
    icon: faBan,
    variant: 'caution',
    label: 'Interrupted'
  },
  completed: {
    icon: faCheck,
    variant: 'success',
    label: 'Completed'
  }
};

export const ADJUDICATION_STATUS_MAP: Record<string, IReviewBadge> = {
  invited: {
    icon: faEnvelope,
    variant: 'neutral',
    label: 'Invited'
  },
  initial: newStatus,
  new: newStatus,
  firstAssessment: inProgressStatus,
  inReview: inReviewStatus,
  submittedFirstAssessment: reviewedStatus,
  awaitingRevision: reviewedStatus,
  revisionAssessment: inProgressStatus,
  revision: inProgressStatus,
  submittedRevisionAssessment: reviewedStatus,
  reviewed: {
    icon: faCheck,
    variant: 'success',
    label: 'Reviewed'
  },
  awaitingRevisionReviewRequest: {
    icon: faEnvelope,
    variant: 'neutral',
    label: 'Awaiting Reinvitation',
    tooltipText:
      'This reviewer completed a review in a previous round: you can reinvite them for this round or exclude them.'
  },
  declined: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined'
  },
  // Separating declined from declinedWithReason (for tooltip) - No new state but only code change
  declinedWithReason: {
    icon: faBan,
    variant: 'caution',
    label: 'Declined',
    tooltipText: 'This Reviewer has sent information about this decision.'
  },

  extensionRequested: {
    icon: faClock,
    variant: 'attention',
    label: 'Extension Requested'
  },
  expired: {
    icon: faBan,
    variant: 'caution',
    label: 'Expired',
    tooltipText: 'This Reviewer did not respond to the invitation.'
  },
  closed: {
    icon: faBan,
    variant: 'caution',
    label: 'Closed'
  },

  // TODO: [COMA-1405] - Test this after Rescinded is implemented
  rescinded: {
    icon: faFileExport,
    variant: 'attention',
    label: 'Rescinded'
  },

  // TODO: [COMA-1405] - Test this after Draft is implemented
  draft: {
    icon: faPen,
    variant: 'neutral',
    label: 'Draft'
  },
  assessment: inProgressStatus,
  interrupted: {
    icon: faBan,
    variant: 'caution',
    label: 'Interrupted'
  },
  completed: {
    icon: faCheck,
    variant: 'success',
    label: 'Completed'
  }
};

export const DUPLICATE_REVIEWER_INVITE_ERROR_MESSAGES = {
  AUTHOR:
    'This person is listed as an author of this submission and cannot be invited as a reviewer.',
  REVIEWER:
    'This Reviewer is already associated with this Submission. Reinvite them or manage them in the Reviews tab.',
  OPPOSED_REVIEWER: '', // TODO: need to get copy from product
  DEFAULT:
    'This person is already associated with this Submission. You cannot be invite them as reviewer.'
};

export const NOTES_SORT_OPTIONS: SortField[] = [
  {
    value: 'modified_at',
    label: 'Date Updated',
    direction: 'desc'
  },
  {
    value: 'created_at',
    label: 'Newest First',
    direction: 'desc'
  },
  {
    value: 'created_at',
    label: 'Oldest First',
    direction: 'asc'
  }
];

export const DECISION_STATUS_MAP: Record<string, IReviewBadge> = {
  minorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Minor Revision'
  },
  majorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Major Revision'
  },
  moderateRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Moderate Revision'
  },
  EditorialSubmissionDecision: {
    variant: 'attention',
    label: 'Editorial Decision',
    icon: faPenToSquare
  },
  SubmissionApprovedForPreProduction: {
    variant: 'success',
    label: 'Pre-Production',
    icon: faCheck
  },
  SubmissionDeadlinePassed: {
    variant: 'attention',
    label: 'Submission Deadline Passed',
    icon: faClock
  },
  EarlyAssessmentReminderTriggered: {
    variant: 'attention',
    label: 'Early Assessment Reminder Triggered',
    icon: faClock
  },
  AdjudicationReminderTriggered: {
    variant: 'attention',
    label: 'Adjudication Reminder Triggered',
    icon: faClock
  },
  ReviewReminderTriggered: {
    variant: 'attention',
    label: 'Review Reminder Triggered',
    icon: faClock
  },
  SubmissionReminderTriggered: {
    variant: 'attention',
    label: 'Submission Reminder Triggered',
    icon: faClock
  },
  LicensingSubmitted: {
    variant: 'attention',
    label: 'Licensing Submitted',
    icon: faPenToSquare
  },
  EditorialSubmissionRejected: {
    variant: 'caution',
    label: 'Rejected',
    icon: faBan
  },
  AuthorChangeRequestCompleted: {
    variant: 'attention',
    label: 'Change Request Completed by Author',
    icon: faPenToSquare
  },
  IntegrationResult: {
    variant: 'attention',
    label: 'Integration Result',
    icon: faPenToSquare
  },
  EditorialChangeRequestSubmitted: {
    variant: 'attention',
    label: 'Change Request Submitted to Author',
    icon: faPenToSquare
  },
  EditorialSubmissionUnsubmitted: {
    variant: 'attention',
    label: 'Unsubmitted',
    icon: faArrowRotateLeft
  },

  // Review Phase & Revision Phase
  ReviewRequested: {
    variant: 'attention',
    label: 'Peer Review Requested',
    icon: faEnvelope
  },
  DeclinedReviewInvitationResent: {
    variant: 'attention',
    label: 'Peer Review Invitation Resent',
    icon: faPenToSquare
  },
  ExpiredReviewInvitationResent: {
    variant: 'attention',
    label: 'Peer Review Invitation Resent',
    icon: faPenToSquare
  },
  WithdrawnReviewInvitationResent: {
    variant: 'attention',
    label: 'Peer Review Invitation Resent',
    icon: faPenToSquare
  },
  PeerReviewDecision: {
    variant: 'attention',
    label: 'Peer Review Decision',
    icon: faPenToSquare
  },
  CoReviewDecision: {
    variant: 'attention',
    label: 'Co-Reviewer Decision',
    icon: faPenToSquare
  },
  FirstAssessmentSubmitted: {
    variant: 'attention',
    label: 'First Assessment Submitted',
    icon: faPenToSquare
  },
  QueueToggled: {
    variant: 'attention',
    label: 'Peer Review Queue Toggled',
    icon: faPenToSquare
  },
  MinReviewCountSet: {
    variant: 'attention',
    label: 'Recommended Number of Reviews',
    icon: faPenToSquare
  },
  ReviewRated: {
    variant: 'attention',
    label: 'Peer Review Rated',
    icon: faPenToSquare
  },
  RescindedFirstAssessment: {
    variant: 'attention',
    label: 'Rescinded Reviews',
    icon: faPenToSquare
  },
  PeerReviewModerated: {
    variant: 'attention',
    label: 'Peer Review Moderated',
    icon: faPenToSquare
  },
  ReviewDeadlineExtensionRequested: {
    variant: 'attention',
    label: 'Review Deadline Extension Requested',
    icon: faClock
  },
  ReviewDeadlineExtended: {
    variant: 'attention',
    label: 'Review Deadline Extended',
    icon: faClock
  },
  ReviewInRevisionRequested: {
    variant: 'attention',
    label: 'Review In Revision Requested',
    icon: faPenToSquare
  },
  ReviewDeadlinePassed: {
    variant: 'attention',
    label: 'Review Deadline Passed',
    icon: faClock
  },
  RevisionRequested: {
    variant: 'attention',
    label: 'Revision',
    icon: faPenToSquare
  },
  RevisionSubmitted: {
    variant: 'attention',
    label: 'Revision Submitted',
    icon: faPenToSquare
  },
  RevisionReviewRequested: {
    variant: 'attention',
    label: 'Revision Review Requested',
    icon: faPenToSquare
  },
  RevisionAssessmentSubmitted: {
    variant: 'attention',
    label: 'Revision Assessment Submitted',
    icon: faPenToSquare
  },
  EditorialInvitedReviewInvitationWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn from Peer Review',
    icon: faPenToSquare
  },
  EditorialAcceptedReviewInvitationWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn from Peer Review',
    icon: faPenToSquare
  },
  EditorialAwaitingRevisionReviewInvitationWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn from Peer Review',
    icon: faPenToSquare
  },
  EditorialRevisionInvitedReviewInvitationWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn from Peer Review',
    icon: faPenToSquare
  },
  EditorialRevisionAssessmentReviewInvitationWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn from Peer Review',
    icon: faPenToSquare
  },

  // Submission Deadline Extension
  SubmissionDeadlineExtensionRequested: {
    variant: 'attention',
    label: 'Submission Deadline Extension Requested',
    icon: faClock
  },
  SubmissionDeadlineExtended: {
    variant: 'attention',
    label: 'Submission Deadline Extended',
    icon: faClock
  },

  // Transfer
  EditorialTransferOffered: {
    variant: 'attention',
    label: 'Editorial Transfer Offered',
    icon: faPenToSquare
  },
  TransferOfferAccepted: {
    variant: 'attention',
    label: 'Transfer Offer Accepted',
    icon: faPenToSquare
  },
  TransferDeclined: {
    variant: 'attention',
    label: 'Transfer Declined',
    icon: faPenToSquare
  },

  // Withdrawal
  AuthorSubmissionWithdrawalRequested: {
    variant: 'attention',
    label: 'Withdrawal Requested by Author',
    icon: faPenToSquare
  },
  AuthorSubmissionWithdrawalRequestRevoked: {
    variant: 'attention',
    label: 'Withdrawal Request Reverted by Author',
    icon: faPenToSquare
  },
  EditorialSubmissionWithdrawn: {
    variant: 'attention',
    label: 'Withdrawn',
    icon: faArchive
  },
  EditorialSubmissionWithdrawalAccepted: {
    variant: 'attention',
    label: 'Withdrawal Request Accepted',
    icon: faPenToSquare
  },
  AcceptedForFormalAcceptance: {
    variant: 'success',
    label: 'Accept',
    icon: faCheck
  },

  // Flags and Tags
  SubmissionTagsReplaced: {
    variant: 'attention',
    label: 'Submission Tags',
    icon: faPenToSquare
  },

  // Author Resubmit submission
  AuthorSubmissionResubmitted: {
    variant: 'attention',
    label: 'Author Resubmit Submission',
    icon: faPenToSquare
  },

  AuthorSubmissionEdited: {
    variant: 'attention',
    label: 'Submission Edited By Author',
    icon: faPenToSquare
  },

  EarlyAssessmentRequestDecision: {
    variant: 'attention',
    label: 'Early Assessment Request Decision',
    icon: faPenToSquare
  },
  EarlyAssessmentRequested: {
    variant: 'attention',
    label: 'Early Assessment Requested',
    icon: faPenToSquare
  },

  EarlyAssessmentSubmitted: {
    variant: 'attention',
    label: 'Early Assessment Submitted',
    icon: faPenToSquare
  },

  AdjudicationRequested: {
    variant: 'attention',
    label: 'Adjudication Requested',
    icon: faPenToSquare
  },
  AdjudicationRequestDecision: {
    variant: 'attention',
    label: 'Adjudication Request Decision',
    icon: faPenToSquare
  },
  AdjudicationSubmitted: {
    variant: 'attention',
    label: 'Adjudication Submitted',
    icon: faPenToSquare
  },
  EditorialCopyrightUploaded: {
    variant: 'attention',
    label: 'Copyright Form Uploaded By Editor',
    icon: faPenToSquare
  },
  EditorialICMJEUploaded: {
    variant: 'attention',
    label: 'Forms and Disclosures Uploaded By Editor',
    icon: faPenToSquare
  },

  AuthorCopyrightSubmitted: {
    variant: 'attention',
    label: 'Copyright Form Submitted By Author',
    icon: faPenToSquare
  },

  // Immediate Revision
  EditorialImmediateRevisionRequested: {
    variant: 'attention',
    label: 'Immediate Revision',
    icon: faPenToSquare
  },

  CoReviewerInvited: {
    variant: 'attention',
    label: 'Co-Reviewer Invited',
    icon: faPenToSquare
  },

  SubmissionDraftCreated: {
    variant: 'attention',
    label: 'Submission draft created',
    icon: faPenToSquare
  },
  SubmissionAccessed: {
    variant: 'attention',
    label: 'Submission accessed',
    icon: faPenToSquare
  },
  ReviewDeadlineExtensionDenied: {
    variant: 'attention',
    label: 'Review Deadline Extension Request Declined',
    icon: faClock
  },
  SubmissionDeadlineExtensionDenied: {
    variant: 'attention',
    label: 'Submission Deadline Extension Request Declined',
    icon: faClock
  },

  PeerReviewInterrupted: {
    variant: 'attention',
    label: 'Peer Review Round Interrupted',
    icon: faPenToSquare
  },
  DecisionReverted: {
    icon: faArrowRotateLeft,
    variant: 'attention',
    label: 'Decision Reversal'
  },
  AuthorCopyrightReset: {
    variant: 'AuthorCopyrightReset',
    label: 'Copyright Form Deleted',
    icon: faPenToSquare
  },
  SubmissionAssignedCOC: {
    variant: 'attention',
    label: 'Submission Assigned COC',
    icon: faPenToSquare
  },
  COCRecommendationModerated: {
    variant: 'attention',
    label: 'COC Recommendation Moderated',
    icon: faPenToSquare
  },
  ContactJournal: {
    variant: 'attention',
    label: 'Contact Journal',
    icon: faEnvelope
  },
  default: {
    icon: faBan,
    variant: 'caution',
    label: 'N/A'
  }
};
